import type { DailyRevenue, ExpenseCategory, ProfitAndLoss } from './types';
import { fijosYVariables, revenueOf } from './finance';
import type { AjusteNaturalezas } from './naturaleza';
import { diasDelMes, diaDeLaSemana } from './ventas';
import { monthOf } from './period';
import { round } from './units';
import { mesEnPalabras } from './repartos';
import type { FormatoCifras, ObjetivosResultado } from './estadoResultados';

/**
 * ANÁLISIS (decidido con Alex el 11 oct 2026): qué está cambiando y dónde
 * mirar. Solo meses cerrados y completos; el mes en curso, aparte y como
 * previsión. Nada de lo que hay aquí se inventa: todo sale del Estado de
 * resultados de cada mes y de las ventas anotadas.
 */

/* ------------------------------------------------------------------ */
/* Punto de equilibrio (regla 3)                                       */
/* ------------------------------------------------------------------ */

export interface Equilibrio {
  estado: 'ok' | 'sin-ventas' | 'sin-margen' | 'no-cuadra';
  ventas: number;
  /** Gastos fijos del mes: personal, renta, seguros, software, contador, teléfono, desgaste, intereses… */
  fijos: number;
  /** Gastos variables del mes: materia prima (consumo), comisiones, luz, gas… */
  variables: number;
  /** Lo que deja cada peso vendido = 1 − variables ÷ ventas. */
  dejaCadaPeso: number;
  /** Equilibrio del mes = fijos ÷ lo que deja cada peso. */
  equilibrioMes: number;
  /** Días que abriste ese mes (días con ventas). */
  diasAbiertos: number;
  /** Equilibrio por día = equilibrio del mes ÷ días que abriste. */
  equilibrioDia: number;
  /** Lo que vendiste de media cada día que abriste. */
  ventaMediaDia: number;
  /** Cuánto por encima (+) o por debajo (−) del equilibrio, por día y en el mes. */
  diferenciaDia: number;
  diferenciaMes: number;
  /**
   * COMPROBACIÓN: (ventas − equilibrio) × lo que deja cada peso tiene que
   * dar la ganancia neta del Estado de resultados (± 1 %). Si no, la
   * clasificación de fijos y variables está mal y el número no se enseña.
   */
  comprobacion: { gananciaNeta: number; calculada: number; cuadra: boolean };
}

/** Días con ventas de un mes (consolidado: dos locales el mismo día = un día). */
export function diasConVentas(revenues: Array<Pick<DailyRevenue, 'date' | 'inHouse' | 'delivery'>>, month?: string): number {
  const fechas = new Set<string>();
  for (const r of revenues) {
    if (month && !r.date.startsWith(month)) continue;
    if (revenueOf(r as DailyRevenue) > 0) fechas.add(r.date);
  }
  return fechas.size;
}

export function puntoDeEquilibrio(pnl: ProfitAndLoss, diasAbiertos: number, settings?: AjusteNaturalezas): Equilibrio {
  const ventas = pnl.revenue;
  const { fijos, variables } = fijosYVariables(pnl, settings);
  const base: Equilibrio = {
    estado: 'ok', ventas, fijos, variables, dejaCadaPeso: 0, equilibrioMes: 0, diasAbiertos,
    equilibrioDia: 0, ventaMediaDia: 0, diferenciaDia: 0, diferenciaMes: 0,
    comprobacion: { gananciaNeta: pnl.netProfit, calculada: 0, cuadra: false },
  };
  if (!(ventas > 0)) return { ...base, estado: 'sin-ventas' };
  const deja = 1 - variables / ventas;
  if (deja <= 0) return { ...base, dejaCadaPeso: round(deja, 6), estado: 'sin-margen' };
  const equilibrioMes = fijos / deja;
  const dias = Math.max(1, diasAbiertos);
  const calculada = (ventas - equilibrioMes) * deja;
  const tolerancia = Math.max(Math.abs(pnl.netProfit) * 0.01, 1);
  const cuadra = Math.abs(calculada - pnl.netProfit) <= tolerancia;
  return {
    ...base,
    estado: cuadra ? 'ok' : 'no-cuadra',
    dejaCadaPeso: round(deja, 6),
    equilibrioMes: round(equilibrioMes, 2),
    equilibrioDia: round(equilibrioMes / dias, 2),
    ventaMediaDia: round(ventas / dias, 2),
    diferenciaDia: round((ventas - equilibrioMes) / dias, 2),
    diferenciaMes: round(ventas - equilibrioMes, 2),
    comprobacion: { gananciaNeta: pnl.netProfit, calculada: round(calculada, 2), cuadra },
  };
}

/* ------------------------------------------------------------------ */
/* Ventas: qué días venden más (regla 5) y previsión (regla 4)         */
/* ------------------------------------------------------------------ */

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DIAS_PLURAL = ['domingos', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados'];

export interface VentaPorDiaSemana {
  /** 0 = domingo … 6 = sábado. */
  dia: number;
  /** «Lunes». */
  nombre: string;
  /** Venta media de los días abiertos (con ventas). */
  media: number;
  dias: number;
  /** ¿Llega al equilibrio diario? (sin equilibrio: undefined) */
  llega?: boolean;
}

/** Venta media de cada día de la semana, solo días abiertos, de lunes a domingo. */
export function ventasPorDiaSemana(revenues: DailyRevenue[], meses: string[], equilibrioDia?: number): VentaPorDiaSemana[] {
  const porFecha = new Map<string, number>();
  for (const r of revenues) {
    if (!meses.includes(monthOf(r.date))) continue;
    porFecha.set(r.date, (porFecha.get(r.date) ?? 0) + revenueOf(r));
  }
  const suma = Array(7).fill(0);
  const cuenta = Array(7).fill(0);
  for (const [fecha, venta] of porFecha) {
    if (!(venta > 0)) continue;
    const d = diaDeLaSemana(fecha);
    suma[d] += venta;
    cuenta[d] += 1;
  }
  return [1, 2, 3, 4, 5, 6, 0].map((dia) => {
    const media = cuenta[dia] ? round(suma[dia] / cuenta[dia], 2) : 0;
    return {
      dia, nombre: DIAS[dia], media, dias: cuenta[dia],
      ...(equilibrioDia && equilibrioDia > 0 && cuenta[dia] ? { llega: media >= equilibrioDia } : {}),
    };
  });
}

export interface Prevision {
  month: string;
  /** Día de hoy dentro del mes. */
  dia: number;
  llevas: number;
  diasAbiertosHastaHoy: number;
  diasQueAbrira: number;
  /** Venta media por día abierto × días que abrirá, redondeado a miles. */
  cerrariasEn: number;
}

/**
 * «A este ritmo cerrarías en unos $49,000»: venta media por día abierto hasta
 * hoy × días que abrirá en el mes (todos menos sus días de cierre).
 */
export function previsionDelMes(revenues: DailyRevenue[], hoy: string, closedWeekdays?: number[]): Prevision | null {
  const month = monthOf(hoy);
  const delMes = revenues.filter((r) => r.date.startsWith(month) && r.date <= hoy);
  const llevas = delMes.reduce((a, r) => a + revenueOf(r), 0);
  const abiertos = diasConVentas(delMes, month);
  if (!(llevas > 0) || abiertos === 0) return null;
  const cerrados = new Set(delMes.filter((r) => r.closed).map((r) => r.date));
  const diasQueAbrira = diasDelMes(month).filter((d) => !closedWeekdays?.includes(diaDeLaSemana(d)) && !cerrados.has(d)).length || abiertos;
  const cerrariasEn = Math.round(((llevas / abiertos) * Math.max(diasQueAbrira, abiertos)) / 1000) * 1000;
  return { month, dia: Number(hoy.slice(8, 10)), llevas: round(llevas, 2), diasAbiertosHastaHoy: abiertos, diasQueAbrira, cerrariasEn };
}

/* ------------------------------------------------------------------ */
/* Gastos: «Lo que más cambió» (regla 6)                               */
/* ------------------------------------------------------------------ */

export interface CambioCategoria {
  clave: string;
  categoria?: ExpenseCategory;
  propia?: string;
  /** Grupo de la línea (para «Materia prima › Comida»). */
  grupo: string;
  ultimo: number;
  media: number;
  diferencia: number;
  /** Cambio en % contra la media (sin media: null). */
  cambioPct: number | null;
}

/** Importe de cada línea (categoría o categoría propia) de un mes. */
function lineasDelMes(pnl: ProfitAndLoss): Map<string, { categoria?: ExpenseCategory; propia?: string; grupo: string; importe: number }> {
  const out = new Map<string, { categoria?: ExpenseCategory; propia?: string; grupo: string; importe: number }>();
  for (const l of pnl.lineas ?? []) {
    if (l.grupo === 'financieros' || l.compartidos) continue;
    const clave = l.propia ? `+${l.propia}` : String(l.categoria);
    const prev = out.get(clave);
    out.set(clave, { categoria: l.categoria, propia: l.propia, grupo: l.grupo, importe: round((prev?.importe ?? 0) + l.importe, 2) });
  }
  return out;
}

/**
 * Todas las categorías con su último mes cerrado contra su media del
 * periodo, ordenadas por la diferencia en dinero. «Lo que más cambió» enseña
 * las 5 primeras; «Ver todas las categorías», la lista entera.
 */
export function loQueMasCambio(meses: Array<{ month: string; pnl: ProfitAndLoss }>): CambioCategoria[] {
  if (meses.length === 0) return [];
  const porMes = meses.map((m) => lineasDelMes(m.pnl));
  const ultimo = porMes[porMes.length - 1];
  const claves = new Set(porMes.flatMap((m) => [...m.keys()]));
  const out: CambioCategoria[] = [];
  for (const clave of claves) {
    const valores = porMes.map((m) => m.get(clave)?.importe ?? 0);
    const media = round(valores.reduce((a, b) => a + b, 0) / valores.length, 2);
    const u = ultimo.get(clave)?.importe ?? 0;
    const info = porMes.map((m) => m.get(clave)).find(Boolean)!;
    out.push({
      clave,
      ...(info.categoria ? { categoria: info.categoria } : {}),
      ...(info.propia ? { propia: info.propia } : {}),
      grupo: info.grupo,
      ultimo: u,
      media,
      diferencia: round(u - media, 2),
      cambioPct: media > 0 ? round(((u - media) / media) * 100, 1) : null,
    });
  }
  return out.sort((a, b) => Math.abs(b.diferencia) - Math.abs(a.diferencia));
}

/* ------------------------------------------------------------------ */
/* «Lo primero que miraría» (regla 2)                                  */
/* ------------------------------------------------------------------ */

export interface Hallazgo {
  clave: string;
  /** Impacto en dinero AL MES, calculado. Ordena los hallazgos. */
  impactoMes: number;
  /** Lo que se enseña arriba: «~$1,020 al mes», «~$880 por lunes». */
  impactoTexto: string;
  /** El dato. */
  titulo: string;
  /** Qué significa y la pregunta (tono de Alexia; nunca «grave»). */
  detalle: string;
  accion: { texto: string; ruta: string };
}

export interface HallazgosInput {
  /** Meses cerrados del periodo, del más viejo al más nuevo, con su calidad. */
  meses: Array<{ month: string; pnl: ProfitAndLoss; completo: boolean }>;
  objetivos?: ObjetivosResultado;
  /** Equilibrio del último mes completo. */
  equilibrio?: Equilibrio;
  porDiaSemana?: VentaPorDiaSemana[];
  nombreCategoria: (c: ExpenseCategory) => string;
  fmt: FormatoCifras;
  /** Mínimo de dinero al mes para que un hallazgo salga (por defecto 100). */
  minimo?: number;
}

const pctDe = (importe: number, ventas: number) => (ventas > 0 ? round((importe / ventas) * 100, 1) : 0);

/** «33 %, 34 % y 32 %». */
const enumerar = (textos: string[]) => (textos.length <= 1 ? textos.join('') : `${textos.slice(0, -1).join(', ')} y ${textos[textos.length - 1]}`);

/**
 * Hasta 3 hallazgos, con reglas fijas (nada de IA) y su impacto en dinero al
 * mes; se ordenan por ese impacto y solo salen los que pasan del mínimo.
 * Solo usan meses completos (ventas de todos los días y recuento).
 *
 *  1. Un grupo (materia prima, personal) por encima de su objetivo 2 o más
 *     meses seguidos: puntos de más × ventas ÷ 100.
 *  2. Una categoría que sube más de un 15 % contra su media: la diferencia.
 *  3. Un día de la semana que no llega al equilibrio diario: lo que le falta
 *     cada día × cuántos hay en un mes.
 */
export function hallazgos(input: HallazgosInput): Hallazgo[] {
  const { fmt } = input;
  const minimo = input.minimo ?? 100;
  const completos = input.meses.filter((m) => m.completo);
  const out: Hallazgo[] = [];
  const aprox = (n: number) => `~${fmt.money(Math.round(n))}`;

  // 1. Grupos por encima del objetivo, meses seguidos hasta el último completo.
  const grupos: Array<{ clave: string; nombre: string; objetivo?: number; importe: (p: ProfitAndLoss) => number; accion: Hallazgo['accion'] }> = [
    { clave: 'materia_prima', nombre: 'La materia prima', objetivo: input.objetivos?.foodCostPct, importe: (p) => p.materialConsumption, accion: { texto: 'Ver qué plato te conviene más', ruta: '/carta?ver=conviene' } },
    { clave: 'personal', nombre: 'El personal', objetivo: input.objetivos?.laborCostPct, importe: (p) => p.laborCost, accion: { texto: 'Ver en Gastos', ruta: '/gastos' } },
  ];
  const ultimoCompleto = completos[completos.length - 1];
  for (const g of grupos) {
    if (g.objetivo == null || !ultimoCompleto) continue;
    const racha: Array<{ month: string; pct: number; ventas: number }> = [];
    for (let i = input.meses.length - 1; i >= 0; i--) {
      const m = input.meses[i];
      if (!m.completo) break;
      const pct = pctDe(g.importe(m.pnl), m.pnl.revenue);
      if (!(m.pnl.revenue > 0) || pct <= g.objetivo) break;
      racha.unshift({ month: m.month, pct, ventas: m.pnl.revenue });
    }
    if (racha.length < 2) continue;
    const ultimo = racha[racha.length - 1];
    const puntos = ultimo.pct - g.objetivo;
    const porPunto = ultimo.ventas / 100;
    const impacto = puntos * porPunto;
    out.push({
      clave: `grupo-${g.clave}`,
      impactoMes: round(impacto, 2),
      impactoTexto: `${aprox(impacto)} al mes`,
      titulo: `${g.nombre} lleva ${racha.length} meses por encima de tu objetivo: ${enumerar(racha.map((r) => fmt.pct(Math.round(r.pct))))}, cuando tu objetivo es ${fmt.pct(g.objetivo)}.`,
      detalle: `Cada punto de más te cuesta unos ${fmt.money(Math.round(porPunto))} al mes. ¿Qué cambió en ${mesEnPalabras(racha[0].month)}?`,
      accion: g.accion,
    });
  }

  // 2. Categorías que suben más de un 15 % contra su media de los meses completos.
  if (completos.length >= 2) {
    const cambios = loQueMasCambio(completos.map(({ month, pnl }) => ({ month, pnl })));
    for (const c of cambios) {
      if (c.grupo === 'materia_prima' && input.objetivos?.foodCostPct != null && out.some((h) => h.clave === 'grupo-materia_prima')) continue;
      if (c.cambioPct == null || c.cambioPct <= 15 || c.diferencia <= 0) continue;
      const nombre = c.propia ?? input.nombreCategoria(c.categoria!);
      out.push({
        clave: `categoria-${c.clave}`,
        impactoMes: c.diferencia,
        impactoTexto: `+${fmt.money(Math.round(c.diferencia))} al mes`,
        titulo: `${nombre} subió un ${fmt.pct(Math.round(c.cambioPct))}.`,
        detalle: `${fmt.money(Math.round(c.ultimo))} en ${mesEnPalabras(ultimoCompleto.month)}; tu media de los ${completos.length} meses es ${fmt.money(Math.round(c.media))}. ¿Es una tarifa nueva o algo que gasta de más?`,
        accion: { texto: 'Ver en Gastos', ruta: c.categoria ? `/gastos?categoria=${c.categoria}` : '/gastos' },
      });
    }
  }

  // 3. Días de la semana que no llegan al equilibrio diario.
  const eq = input.equilibrio;
  if (eq && eq.estado === 'ok' && eq.equilibrioDia > 0) {
    for (const d of input.porDiaSemana ?? []) {
      if (!d.dias || d.media >= eq.equilibrioDia) continue;
      const falta = eq.equilibrioDia - d.media;
      // Cuántos de ese día hay en un mes, de media en el periodo.
      const alMes = d.dias / Math.max(1, completos.length);
      out.push({
        clave: `dia-${d.dia}`,
        impactoMes: round(falta * alMes, 2),
        impactoTexto: `${aprox(falta)} por ${DIAS[d.dia].toLowerCase()}`,
        titulo: `Los ${DIAS_PLURAL[d.dia]} vendes ${fmt.money(Math.round(d.media))} de media.`,
        detalle: `Es menos de lo que necesitas vender cada día que abres (${fmt.money(Math.round(eq.equilibrioDia))}). ¿Te compensa abrir los ${DIAS_PLURAL[d.dia]}?`,
        accion: { texto: 'Ver ventas por día', ruta: '#dias' },
      });
    }
  }

  return out
    .filter((h) => h.impactoMes >= minimo)
    .sort((a, b) => b.impactoMes - a.impactoMes)
    .slice(0, 3);
}
